import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Image as ImageIcon, X, Check } from "lucide-react";
import { api, resolveMediaUrl } from "@/lib/api";

/**
 * Pilih foto dari Admin → Galeri (bukan upload baru).
 * onChange({ image, galleryId })
 */
const GalleryPicker = ({
  value = "",
  galleryId = "",
  onChange,
  label = "Foto dari Galeri",
  aspect = "wide",
}) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [album, setAlbum] = useState("__all__");
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/gallery")
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const albums = useMemo(
    () => ["__all__", ...Array.from(new Set(items.map((g) => g.album).filter(Boolean)))],
    [items]
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((g) => {
      if (album !== "__all__" && g.album !== album) return false;
      if (!needle) return true;
      return `${g.title} ${g.album}`.toLowerCase().includes(needle);
    });
  }, [items, album, q]);

  const aspectClass = { square: "aspect-square", wide: "aspect-[16/9]", portrait: "aspect-[3/4]" }[aspect] || "aspect-[16/9]";
  const preview = value ? resolveMediaUrl(value) : "";

  const pick = (g) => {
    onChange({ image: resolveMediaUrl(g.image), galleryId: g.id });
    setOpen(false);
    setQ("");
  };

  const clear = () => onChange({ image: "", galleryId: "" });

  return (
    <div>
      {label && <label className="block text-sm font-semibold text-zinc-800 mb-2">{label}</label>}
      <div className={`relative ${aspectClass} rounded-2xl border-2 border-dashed border-zinc-200 bg-zinc-50 overflow-hidden group hover:border-maroon-400 transition`}>
        {preview ? (
          <>
            <img src={preview} alt="preview" className="absolute inset-0 w-full h-full object-contain p-2 bg-zinc-50" />
            <button
              type="button"
              onClick={clear}
              className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/95 text-red-600 flex items-center justify-center shadow-md"
              aria-label="Hapus"
            >
              <X size={14} />
            </button>
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="absolute bottom-2 right-2 px-3 py-1.5 rounded-full bg-white/95 text-zinc-800 text-xs font-semibold shadow-md"
            >
              Ganti dari Galeri
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="absolute inset-0 w-full h-full flex flex-col items-center justify-center gap-2 text-zinc-500 hover:text-maroon-700"
          >
            <ImageIcon size={22} />
            <span className="text-xs font-semibold">Pilih dari Galeri</span>
            <span className="text-[10px] text-zinc-400">Foto yang sudah diunggah di menu Galeri</span>
          </button>
        )}
      </div>
      {galleryId && <p className="mt-1 text-[10px] text-zinc-400">Terhubung ke foto galeri</p>}

      {open && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b">
              <div>
                <h3 className="text-lg font-display font-bold">Pilih foto galeri</h3>
                <p className="text-xs text-zinc-500 mt-0.5">Sumber: Admin → Galeri</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="p-2 rounded-lg hover:bg-zinc-100">
                <X size={18} />
              </button>
            </div>

            <div className="px-5 pt-4 flex flex-wrap gap-2">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cari judul..."
                className="flex-1 min-w-[160px] px-3 py-2 rounded-xl border border-zinc-200 text-sm outline-none focus:border-maroon-700"
              />
              <select
                value={album}
                onChange={(e) => setAlbum(e.target.value)}
                className="px-3 py-2 rounded-xl border border-zinc-200 text-sm outline-none"
              >
                {albums.map((a) => (
                  <option key={a} value={a}>{a === "__all__" ? "Semua album" : a}</option>
                ))}
              </select>
            </div>

            <div className="p-5 overflow-y-auto flex-1">
              {loading && <p className="text-sm text-zinc-500 text-center py-10">Memuat galeri...</p>}
              {!loading && filtered.length === 0 && (
                <div className="text-center py-10 text-sm text-zinc-500">
                  Belum ada foto di galeri.{" "}
                  <Link to="/admin/gallery" className="text-maroon-800 font-semibold underline">Upload di Galeri</Link>
                </div>
              )}
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {filtered.map((g) => {
                  const active = galleryId === g.id || value === g.image || value === resolveMediaUrl(g.image);
                  return (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => pick(g)}
                      className={`relative aspect-square rounded-xl overflow-hidden bg-zinc-100 border-2 ${active ? "border-maroon-700 ring-2 ring-maroon-200" : "border-transparent hover:border-maroon-300"}`}
                    >
                      <img src={resolveMediaUrl(g.image)} alt={g.title} className="w-full h-full object-cover" />
                      {active && (
                        <span className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-maroon-800 text-white flex items-center justify-center">
                          <Check size={12} />
                        </span>
                      )}
                      <span className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] px-1.5 py-1 truncate">{g.title}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPicker;
